import React from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import blogPosts from "../data/blogPosts";

const HomePage = () => {
  return (
    <>
      {/* ===== HERO ===== */}
      <section className="hero" id="hero" aria-label="Introduction">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <p className="section-label">Product-Driven Tech Studio</p>
              <h1>We Build AI-Powered Digital Products That Actually Ship</h1>
              <p className="hero-sub">
                SaaS platforms, AI systems, and blockchain applications —
                designed, built, and scaled by a product-first engineering
                studio.
              </p>
              <div className="hero-actions">
                <a href="#contact" className="btn btn-primary">
                  Book a Technical Consultation
                </a>
                <a href="#products" className="btn btn-secondary">
                  Explore Our Products
                </a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-image-wrap">
                <img
                  src="/handcloud.jpeg"
                  alt="Modern technology workstation with glowing monitor displaying data visualizations"
                  width="460"
                  height="320"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section className="about" id="about" aria-label="About Hayden Bulk Tech Labs">
        <div className="container">
          <div className="about-grid">
            <div className="about-text">
              <p className="section-label">About Us</p>
              <h2>A Studio That Ships Production Systems</h2>
              <p>
                Hayden Bulk Tech Labs is a product-driven technology studio
                focused on building real, revenue-ready digital products.
              </p>
              <p>
                We don't just consult — we design, ship, iterate, and operate
                production systems across AI, SaaS, blockchain, and mobile
                platforms.
              </p>
              <p>
                Our work spans AI-powered content platforms, Web3 games and
                on-chain systems, virtual try-on and computer vision products,
                and consumer and B2B SaaS applications.
              </p>
            </div>
            <div>
              <div className="about-highlights">
                <div className="about-highlight">
                  <h4>AI-Powered Solutions</h4>
                  <p>
                    Language models, computer vision, and intelligent automation
                    integrated into real products.
                  </p>
                </div>
                <div className="about-highlight">
                  <h4>Web3 &amp; Blockchain</h4>
                  <p>
                    On-chain systems, smart contracts, and token-based economies
                    built for production.
                  </p>
                </div>
                <div className="about-highlight">
                  <h4>Scalable Architecture</h4>
                  <p>
                    Cloud-native infrastructure designed to handle growth from
                    day one.
                  </p>
                </div>
                <div className="about-highlight">
                  <h4>Full-Stack Execution</h4>
                  <p>
                    From product design through deployment and operations — end
                    to end.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ===== PRODUCTS ===== */}
      <section className="products" id="products" aria-label="Our products">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Products</p>
            <h2>What We've Built</h2>
            <p className="section-intro">
              Production systems serving real users — not prototypes or pitch
              decks.
            </p>
          </div>

          <div className="product-grid">
            {/* ChattyBooks */}
            <div className="product-card">
              <div className="product-logo">
                <img
                  src="/chattybook-logo.png"
                  alt="ChattyBooks logo"
                  width="72"
                  height="72"
                  loading="lazy"
                />
              </div>
              <div className="product-content">
                <h3>ChattyBooks</h3>
                <p className="product-tagline">
                  AI-Powered Interactive Reading Platform
                </p>
                <p className="product-desc">
                  An AI-driven platform that transforms the reading experience
                  into an interactive, personalized journey. ChattyBooks uses
                  language models to generate adaptive content, enabling users to
                  engage with stories in ways that static text never could.
                </p>
                <ul className="product-features">
                  <li>AI-generated interactive narratives</li>
                  <li>Personalized reading experiences</li>
                  <li>Real-time content adaptation</li>
                  <li>Multi-format story support</li>
                  <li>Intelligent content recommendations</li>
                  <li>Cross-platform accessibility</li>
                </ul>
                <div className="product-meta">
                  <a
                    href="https://www.chattybooks.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit ChattyBooks
                  </a>
                  <span className="product-platform">Web Platform</span>
                </div>
              </div>
            </div>

            {/* PictureMeIn */}
            <div className="product-card">
              <div className="product-logo">
                <img
                  src="/picturemein-logo.png"
                  alt="PictureMeIn logo"
                  width="72"
                  height="72"
                  loading="lazy"
                />
              </div>
              <div className="product-content">
                <h3>PictureMeIn</h3>
                <p className="product-tagline">
                  AI Virtual Try-On &amp; Image Transformation Platform
                </p>
                <p className="product-desc">
                  A computer vision platform that lets users virtually place
                  themselves into any scene or try on products using AI-powered
                  image transformation. PictureMeIn handles face detection,
                  scene composition, and photorealistic rendering in real time.
                </p>
                <ul className="product-features">
                  <li>AI-powered face detection and mapping</li>
                  <li>Virtual try-on for products</li>
                  <li>Photorealistic scene composition</li>
                  <li>Real-time image transformation</li>
                  <li>High-resolution output rendering</li>
                  <li>API for third-party integration</li>
                </ul>
                <div className="product-meta">
                  <a
                    href="https://www.picturemein.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit PictureMeIn
                  </a>
                  <span className="product-platform">Web Platform</span>
                </div>
              </div>
            </div>

            {/* Farty Unicorn */}
            <div className="product-card">
              <div
                className="product-logo"
                style={{ background: "#1a1028" }}
              >
                <img
                  src="/farty-unicorn-banner.png"
                  alt="Farty Unicorn game banner"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>Farty Unicorn</h3>
                <p className="product-tagline">
                  Blockchain-Powered Mobile Game
                </p>
                <p className="product-desc">
                  A Web3 mobile game that combines engaging gameplay with
                  blockchain-based mechanics. Farty Unicorn features on-chain
                  assets, token-based rewards, and decentralized game economies
                  — proving that blockchain gaming can be fun and accessible.
                </p>
                <ul className="product-features">
                  <li>On-chain game assets and NFTs</li>
                  <li>Token-based reward system</li>
                  <li>Decentralized game economy</li>
                  <li>Cross-platform mobile gameplay</li>
                  <li>Smart contract integration</li>
                  <li>Community-driven development</li>
                </ul>
                <div className="product-meta">
                  <span
                    className="btn btn-ghost"
                    style={{ cursor: "default" }}
                  >
                    Mobile App
                  </span>
                  <span className="product-platform">Mobile / Web3</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="services" id="services" aria-label="Our services">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Services</p>
            <h2>What We Do</h2>
            <p className="section-intro">
              End-to-end technical capabilities for companies building digital
              products.
            </p>
          </div>

          <div className="services-grid">
            <div className="service-item">
              <div className="service-icon" aria-hidden="true">
                &#9670;
              </div>
              <h3>SaaS Product Development</h3>
              <p>
                Full-stack design, development, and deployment of
                subscription-based software platforms from zero to production.
              </p>
            </div>
            <div className="service-item">
              <div className="service-icon" aria-hidden="true">
                &#9671;
              </div>
              <h3>AI &amp; Machine Learning Solutions</h3>
              <p>
                Custom AI integrations, language model applications, computer
                vision systems, and intelligent automation pipelines.
              </p>
            </div>
            <div className="service-item">
              <div className="service-icon" aria-hidden="true">
                &#9674;
              </div>
              <h3>Blockchain &amp; Web3 Development</h3>
              <p>
                Smart contracts, token economies, on-chain applications, and
                decentralized system architecture.
              </p>
            </div>
            <div className="service-item">
              <div className="service-icon" aria-hidden="true">
                &#9673;
              </div>
              <h3>Technical Consulting</h3>
              <p>
                Architecture reviews, technology selection, scaling strategies,
                and hands-on technical advisory for product teams.
              </p>
            </div>
            <div className="service-item">
              <div className="service-icon" aria-hidden="true">
                &#9672;
              </div>
              <h3>Product &amp; Technical Marketing</h3>
              <p>
                Go-to-market strategy, technical content, developer
                documentation, and product positioning for technical audiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="why-us" id="why-us" aria-label="Why work with us">
        <div className="container">
          <div className="why-grid">
            <div>
              <p className="section-label">Why Us</p>
              <h2>Built Different</h2>
              <p className="section-intro" style={{ marginBottom: "32px" }}>
                We operate like a product company, not an agency. Every
                engagement is treated like our own product launch.
              </p>
              <ul className="why-list">
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Product-first mindset</strong>
                    <br />
                    <span>
                      We think in outcomes and user value, not billable hours or
                      feature checklists.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>AI &amp; blockchain fluency</strong>
                    <br />
                    <span>
                      Deep, production-level experience with the technologies
                      that matter.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>End-to-end execution</strong>
                    <br />
                    <span>
                      From architecture and design through deployment,
                      monitoring, and iteration.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Modern scalable stacks</strong>
                    <br />
                    <span>
                      React, Node, AWS, Solana, and AI/ML tooling — chosen for
                      the problem, not the trend.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="why-check" aria-hidden="true">
                    &#10003;
                  </span>
                  <div>
                    <strong>Founder-level involvement</strong>
                    <br />
                    <span>
                      Senior engineers on every project. No handoffs to junior
                      teams.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="why-image">
              <img
                src="/canadamotherboard.jpeg"
                alt="Close-up of modern server hardware and circuit board technology"
                width="540"
                height="400"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== ENGAGEMENT MODELS ===== */}
      <section
        className="engagement"
        id="engagement"
        aria-label="Engagement models"
      >
        <div className="container">
          <div className="section-header">
            <p className="section-label">Engagement Models</p>
            <h2>How We Work</h2>
            <p className="section-intro">
              Flexible structures designed for different stages and scopes.
            </p>
          </div>

          <div className="engagement-grid">
            <div className="engagement-card">
              <div className="engagement-num">01</div>
              <h3>Fixed-Scope MVP Builds</h3>
              <p>
                Defined scope, timeline, and deliverables. Ideal for validating
                a product concept and getting to market.
              </p>
            </div>
            <div className="engagement-card">
              <div className="engagement-num">02</div>
              <h3>Monthly Retainers</h3>
              <p>
                Ongoing development capacity for teams that need consistent
                engineering support and iteration cycles.
              </p>
            </div>
            <div className="engagement-card">
              <div className="engagement-num">03</div>
              <h3>Technical Advisory</h3>
              <p>
                Strategic guidance on architecture, AI integration, blockchain
                strategy, and technology decisions.
              </p>
            </div>
            <div className="engagement-card">
              <div className="engagement-num">04</div>
              <h3>Co-Development Partnerships</h3>
              <p>
                Shared ownership and equity-based partnerships for founders
                building ambitious technical products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BLOG PREVIEW ===== */}
      <section className="blog-section" id="blog" aria-label="Blog">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Blog</p>
            <h2>Insights &amp; Technical Writing</h2>
            <p className="section-intro">
              Practical perspectives on building AI-powered products, SaaS
              development, and emerging technology.
            </p>
          </div>

          <div className="blog-preview">
            {blogPosts.slice(0, 2).map((post) => (
              <div className="blog-card" key={post.slug}>
                <span className="blog-tag">{post.tag}</span>
                <h3>
                  {post.published ? (
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  ) : (
                    <span style={{ color: "var(--white)" }}>{post.title}</span>
                  )}
                </h3>
                <p>{post.description}</p>
                <span className="blog-date">
                  {post.date}
                  {post.readTime ? ` \u00B7 ${post.readTime}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT FORM ===== */}
      <ContactForm />
    </>
  );
};

export default HomePage;
